import React, { useState, useEffect } from "react";
import {
  LoanStatisticsService,
  DefaultRateTrendResponse,
} from "../services/loanStatisticsService";

interface DefaultRateChartProps {
  activeTimeframe: string;
  onTimeframeChange: (timeframe: string) => void;
}

export const DefaultRateChart: React.FC<DefaultRateChartProps> = ({
  activeTimeframe,
  onTimeframeChange,
}) => {
  const timeframes = ["30D", "90D", "12M", "All"];
  const [data, setData] = useState<DefaultRateTrendResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const legendItems = [
    { color: "rgba(0, 212, 255, 0.95)", label: "Alt-A" },
    { color: "rgba(0, 255, 136, 0.95)", label: "Prime" },
    { color: "rgba(255, 184, 0, 0.95)", label: "B-Lend" },
    { color: "rgba(255, 51, 102, 0.95)", label: "Distressed" },
  ];

  // Risk band to color mapping
  const riskBandColors: Record<string, string> = {
    "Alt-A": "rgba(0, 212, 255, 0.95)",
    Prime: "rgba(0, 255, 136, 0.95)",
    "B-Lend": "rgba(255, 184, 0, 0.95)",
    Distressed: "rgba(255, 51, 102, 0.95)",
  };

  // Risk band to stroke style mapping
  const riskBandStyles: Record<
    string,
    { strokeWidth: string; strokeDasharray?: string }
  > = {
    "Alt-A": { strokeWidth: "1.5" },
    Prime: { strokeWidth: "1.2" },
    "B-Lend": { strokeWidth: "1.3", strokeDasharray: "2 3" },
    Distressed: { strokeWidth: "1.4" },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const queryParams: Record<string, string> = {
          timeframe: activeTimeframe,
        };

        const response = await LoanStatisticsService.getDefaultRateTrends(
          undefined,
          queryParams
        );

        setData(response);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch default rate trends";
        setError(errorMessage);
        console.error("Error fetching default rate trends:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [activeTimeframe]);

  // Transform data points to SVG coordinates
  const transformDataPointsToPath = (
    dataPoints: Array<{ x: number; y: number }>,
    chartHeight: number = 60,
    chartWidth: number = 100
  ): string => {
    if (!dataPoints || dataPoints.length === 0) {
      return "";
    }

    // Sort by x value to ensure proper line drawing
    const sortedPoints = [...dataPoints].sort((a, b) => a.x - b.x);

    const minX = Math.min(...sortedPoints.map((p) => p.x));
    const maxX = Math.max(...sortedPoints.map((p) => p.x));
    const rangeX = maxX - minX || 1; // Avoid division by zero

    const maxY = Math.max(...sortedPoints.map((p) => p.y), 0.1); // Avoid division by zero
    const minY = Math.min(...sortedPoints.map((p) => p.y), 0);

    return sortedPoints
      .map((point, index) => {
        // Normalize x to 0-100 range
        const x =
          rangeX > 0
            ? ((point.x - minX) / rangeX) * chartWidth
            : (index / Math.max(sortedPoints.length - 1, 1)) * chartWidth;

        // Normalize y to chart height, with padding
        // Invert Y axis (SVG Y increases downward, but we want higher rates at top)
        const normalizedY =
          maxY > minY ? (point.y - minY) / (maxY - minY) : 0.5; // Center if all values are the same

        const y =
          chartHeight - normalizedY * (chartHeight * 0.7) - chartHeight * 0.15;

        return `${index === 0 ? "M" : "L"} ${x},${y}`;
      })
      .join(" ");
  };

  // Get legend items based on available data
  const getLegendItems = () => {
    if (data && data.data.timeSeries.length > 0) {
      // Show only risk bands that have data
      return data.data.timeSeries.map((series) => ({
        color: riskBandColors[series.riskBand] || "rgba(255, 255, 255, 0.95)",
        label: series.riskBand,
      }));
    }
    // Fallback to default legend items
    return legendItems;
  };

  // Calculate weighted average for current timeframe
  const getWeightedAverage = () => {
    if (!data || !data.data.riskBands || data.data.riskBands.length === 0) {
      return null;
    }

    // Calculate weighted average based on total loans
    let totalWeightedRate = 0;
    let totalLoans = 0;

    data.data.riskBands.forEach((band) => {
      totalWeightedRate += band.defaultRate * band.totalLoans;
      totalLoans += band.totalLoans;
    });

    if (totalLoans === 0) {
      return data.data.weightedAverage90D; // Fallback to API provided value
    }

    return totalWeightedRate / totalLoans;
  };

  return (
    <article className="relative p-4 pb-4.5 rounded-[18px] bg-gradient-to-br from-[#22284d] to-[#10142f] border border-[rgba(139,146,176,0.2)] shadow-[0_16px_40px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2 text-[15px] font-medium tracking-[0.06em] uppercase">
            <span>Default rate trends</span>
            <div className="flex items-center gap-1.5 ml-1">
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: riskBandColors.Prime }}
                title="Prime"
              />
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: riskBandColors["Alt-A"] }}
                title="Alt-A"
              />
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: riskBandColors["B-Lend"] }}
                title="B-Lend"
              />
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: riskBandColors.Distressed }}
                title="Distressed"
              />
            </div>
          </div>
          <div className="text-[11px] text-[#8b92b0]">
            Rolling performance by risk band – updated in near real-time.
          </div>
        </div>
        <div className="inline-flex p-0.75 rounded-full bg-[rgba(7,9,26,0.9)] border border-[rgba(139,146,176,0.6)] text-[11px] gap-0.75">
          {timeframes.map((period) => (
            <button
              key={period}
              onClick={() => onTimeframeChange(period)}
              className={`border-none rounded-full px-2.5 py-1 bg-transparent cursor-pointer text-[11px] transition-all duration-200 ${
                activeTimeframe === period
                  ? "bg-gradient-to-r from-[rgba(0,212,255,0.46)] to-[rgba(139,92,246,0.6)] text-white"
                  : "text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)] hover:text-white"
              }`}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-1.5 rounded-xl bg-gradient-to-b from-[#1f2849] to-[#050716] border border-[rgba(139,146,176,0.35)] p-2.5 pb-3.5 relative overflow-hidden">
        <div className="flex items-center justify-between gap-2.5 mb-1.5 text-[11px] text-[#8b92b0]">
          <div className="flex items-center gap-2.5 flex-wrap">
            {getLegendItems().map((item) => (
              <span
                key={item.label}
                className="inline-flex items-center gap-0.75"
              >
                <span
                  className="w-2.25 h-2.25 rounded-full mr-1"
                  style={{ background: item.color }}
                />
                {item.label}
              </span>
            ))}
          </div>
          <div>
            Weighted avg ({activeTimeframe}):{" "}
            <strong>
              {isLoading
                ? "Loading..."
                : error
                ? "Error"
                : (() => {
                    const avg = getWeightedAverage();
                    return avg !== null
                      ? `${(avg * 100).toFixed(2)}%`
                      : data?.data.weightedAverage90D
                      ? `${(data.data.weightedAverage90D * 100).toFixed(2)}%`
                      : "N/A";
                  })()}
            </strong>
          </div>
        </div>

        <div
          className="h-[230px] relative overflow-hidden rounded-[10px] animate-grid-on"
          style={{
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to right, rgba(255, 255, 255, 0.04) 1px, transparent 1px)`,
            backgroundSize: "0 0",
          }}
        >
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-[#8b92b0] text-sm">
                Loading chart data...
              </div>
            </div>
          ) : error ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-red-400 text-sm">Error: {error}</div>
            </div>
          ) : data && data.data.timeSeries.length > 0 ? (
            <>
              {/* Render gradient fills for each risk band */}
              {data.data.timeSeries.map((series) => {
                const color =
                  riskBandColors[series.riskBand] || "rgba(255, 255, 255, 0.3)";
                const pathData = transformDataPointsToPath(series.dataPoints);
                if (!pathData) return null;

                // Create closed path for gradient fill
                const fillPath = `${pathData} L 100,60 L 0,60 Z`;

                return (
                  <div
                    key={`fill-${series.riskBand}`}
                    className="absolute inset-0 pointer-events-none"
                  >
                    <svg
                      viewBox="0 0 100 60"
                      preserveAspectRatio="none"
                      className="w-full h-full"
                    >
                      <path
                        d={fillPath}
                        fill={color.replace("0.95", "0.15")}
                        opacity="0.9"
                      />
                    </svg>
                  </div>
                );
              })}

              {/* Render chart lines */}
              <div className="absolute inset-0 pointer-events-none">
                <svg
                  viewBox="0 0 100 60"
                  preserveAspectRatio="none"
                  className="w-full h-full"
                >
                  {data.data.timeSeries.map((series) => {
                    const color =
                      riskBandColors[series.riskBand] ||
                      "rgba(255, 255, 255, 0.9)";
                    const style = riskBandStyles[series.riskBand] || {
                      strokeWidth: "1.5",
                    };
                    const pathData = transformDataPointsToPath(
                      series.dataPoints
                    );

                    if (!pathData) return null;

                    return (
                      <path
                        key={series.riskBand}
                        d={pathData}
                        fill="none"
                        stroke={color}
                        strokeWidth={style.strokeWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeDasharray={style.strokeDasharray}
                      />
                    );
                  })}
                </svg>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-[#8b92b0] text-sm">No data available</div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};
